/**
 * A mesa de 4 em Linha da Sala de Descanso — o protocolo.
 *
 * As REGRAS do jogo vivem no `@greenvoice/shared` (`connect4.ts`), puras e sem
 * ideia nenhuma de que existe uma rede. O que está aqui é a outra metade: quem
 * está sentado, de quem é a vez, e como é que dois ecrãs chegam ao MESMO
 * tabuleiro sem haver um servidor a dizer quem tem razão.
 *
 * Não há servidor. Viaja tudo pelo canal de dados do LiveKit, como o quadro
 * branco — e a mesma escolha de fundo: o que se envia é a JOGADA (uma coluna),
 * nunca o tabuleiro. Um tabuleiro em trânsito é uma opinião que pode chegar
 * atrasada e apagar uma jogada boa; um histórico de colunas só cresce, e as
 * regras são determinísticas, por isso quem tem o mesmo histórico tem o mesmo
 * tabuleiro. Sempre.
 *
 * As três coisas que uma call de escritório mesmo faz e que isto tem de aguentar
 * — e como cada uma se resolve SEM um árbitro:
 *
 *  1. O PACOTE REPETIDO ou fora de ordem. Cada jogada leva o número de ordem que
 *     lhe pertence (`n`); se não for o próximo, cai. Uma jogada duplicada tem o
 *     `n` de uma jogada que já entrou, e portanto não faz nada.
 *  2. OS DOIS A SENTAREM-SE NO MESMO LUGAR ao mesmo tempo. Ninguém arbitra:
 *     ganha a identidade menor, regra que toda a gente aplica igual e portanto dá
 *     o mesmo resultado em todos os ecrãs. E mal caia a primeira peça os lugares
 *     CONGELAM — depois de o jogo começar não há como roubar a cadeira a
 *     ninguém, porque já não se aceita reclamação nenhuma.
 *  3. QUEM CHEGA A MEIO. Diz `hello` e recebe um `sync`. Se vierem vários (todos
 *     os presentes respondem), fica o que traz MAIS jogadas: o histórico só
 *     cresce, logo o mais longo é o mais recente. Um `sync` atrasado nunca faz
 *     recuar um jogo a andar.
 *
 * A função é PURA e devolve o MESMO objecto quando nada muda — é o que permite
 * ao React não repintar por um pacote inofensivo, e é a mesma propriedade que o
 * `c4Drop` já garante nas regras.
 */

import { type C4Player, type C4State, c4CanDrop, c4Drop, c4Empty, c4Replay } from "@greenvoice/shared";

/** Um dos dois lugares à mesa. 1 = vermelho (começa), 2 = amarelo. */
export type G4Slot = 1 | 2;

export interface TableState {
  /** Quem está sentado em cada lugar (identidade do LiveKit = `userId`). */
  readonly seats: readonly [string | null, string | null];
  /** O histórico de colunas. É ISTO o estado partilhado; o resto deriva. */
  readonly moves: readonly number[];
  /** O tabuleiro que corresponde ao histórico, mantido a par para não haver um
   *  `c4Replay` a cada pintura. */
  readonly game: C4State;
}

export type G4Msg =
  | { k: "g4:hello"; by: string }
  | { k: "g4:sync"; to: string; seats: [string | null, string | null]; moves: number[] }
  | { k: "g4:sit"; by: string; slot: G4Slot }
  | { k: "g4:stand"; by: string }
  | { k: "g4:mv"; by: string; col: number; n: number }
  | { k: "g4:new"; by: string };

export function g4Empty(): TableState {
  return { seats: [null, null], moves: [], game: c4Empty() };
}

/** Em que lugar está `id`, ou null se está só a ver. */
export function g4SeatOf(state: TableState, id: string | null): G4Slot | null {
  if (!id) return null;
  if (state.seats[0] === id) return 1;
  if (state.seats[1] === id) return 2;
  return null;
}

/** É a vez de `id`? Exige lugar, jogo a decorrer e a vez certa. */
export function g4IsMyTurn(state: TableState, id: string | null): boolean {
  const seat = g4SeatOf(state, id);
  return seat !== null && state.game.outcome === 0 && state.game.turn === seat;
}

/** A mesa está pronta a jogar (dois sentados). */
export function g4Ready(state: TableState): boolean {
  return state.seats[0] !== null && state.seats[1] !== null;
}

function withSeats(state: TableState, seats: readonly [string | null, string | null]): TableState {
  if (seats[0] === state.seats[0] && seats[1] === state.seats[1]) return state;
  return { ...state, seats };
}

/** Tira `id` do lugar onde estiver (uma pessoa não ocupa dois). */
function vacate(seats: readonly [string | null, string | null], id: string): [string | null, string | null] {
  return [seats[0] === id ? null : seats[0], seats[1] === id ? null : seats[1]];
}

/**
 * Aplica uma mensagem. Devolve o MESMO estado quando a mensagem não muda nada
 * — pacote repetido, jogada fora de vez, reclamação de um lugar já ocupado.
 */
export function g4Reduce(state: TableState, m: G4Msg): TableState {
  switch (m.k) {
    case "g4:sit": {
      // Lugares CONGELAM à primeira peça: enquanto ninguém jogou trocam-se à
      // vontade, depois de começar não se mexe. É o que fecha a porta a alguém
      // sentar-se a meio da partida e passar a mandar nas peças de outro.
      if (state.moves.length > 0) return state;
      if (!m.by) return state;
      const i = m.slot - 1;
      const livre = vacate(state.seats, m.by);
      const dono = livre[i] ?? null;
      // Empate de dois a reclamarem o mesmo lugar no mesmo instante: ganha a
      // identidade menor. Não é justiça — é uma regra que TODOS os ecrãs aplicam
      // do mesmo modo, e é isso que faz a mesa ficar igual em todo o lado. Quem
      // perde fica onde já estava: a reclamação falhada não o levanta do lugar.
      if (dono !== null && dono !== m.by && m.by > dono) return state;
      const seats: [string | null, string | null] = [livre[0], livre[1]];
      seats[i] = m.by;
      return withSeats(state, seats);
    }

    case "g4:stand":
      if (!m.by) return state;
      return withSeats(state, vacate(state.seats, m.by));

    case "g4:mv": {
      // Quatro guardas, e nenhuma é decorativa: a mesa cheia, a ordem certa
      // (mata o pacote repetido e o que chega trocado), o dono da vez (mata a
      // jogada de quem não está sentado ou joga fora da vez) e a coluna
      // possível.
      //
      // A primeira parece a mais mole e é a que evita a mesa MORTA: com um só
      // lugar ocupado, a peça caía na mesma, os lugares congelavam à primeira
      // peça, e o segundo lugar ficava fechado para sempre — jogo por acabar,
      // ninguém a poder acabá-lo, e nem um `stand` resolvia porque a vez já era
      // de um lugar sem dono.
      if (!g4Ready(state)) return state;
      if (m.n !== state.moves.length) return state;
      const dono = state.seats[state.game.turn - 1];
      if (!dono || dono !== m.by) return state;
      if (!c4CanDrop(state.game, m.col)) return state;
      return { ...state, moves: [...state.moves, m.col], game: c4Drop(state.game, m.col) };
    }

    case "g4:new": {
      if (state.moves.length === 0) return state;
      if (g4SeatOf(state, m.by) === null) return state;
      return { ...state, moves: [], game: c4Empty() };
    }

    case "g4:sync": {
      // Vários presentes respondem ao mesmo `hello`. Fica o histórico MAIS
      // LONGO: como o histórico só cresce, o mais longo é o mais recente — e
      // assim um `sync` atrasado nunca faz recuar um jogo que já andou.
      const meu = state.moves.length;
      const virgem = meu === 0 && state.seats[0] === null && state.seats[1] === null;
      if (m.moves.length <= meu && !virgem) return state;
      return { seats: [m.seats[0], m.seats[1]], moves: [...m.moves], game: c4Replay(m.moves) };
    }

    case "g4:hello":
      // Quem responde é o componente (é ele que sabe enviar); aqui não muda nada.
      return state;
  }
}

/**
 * Liberta os lugares de quem já não está na call.
 *
 * Ninguém carrega em "levantar-me" antes de fechar o portátil, e um lugar preso
 * a um fantasma é uma mesa que nunca mais serve para nada. A lista de presentes
 * é a mesma em todos os ecrãs (vem do SFU), por isso esta limpeza dá o mesmo
 * resultado em todos — não é preciso mandar pacote nenhum a anunciá-la.
 */
export function g4Prune(state: TableState, present: ReadonlySet<string>): TableState {
  const seats: [string | null, string | null] = [
    state.seats[0] && present.has(state.seats[0]) ? state.seats[0] : null,
    state.seats[1] && present.has(state.seats[1]) ? state.seats[1] : null,
  ];
  return withSeats(state, seats);
}

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : -1;
}

function id(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

/**
 * Valida um pacote cru do canal de dados.
 *
 * Mesma razão do quadro branco: a call é território partilhado e um pacote
 * malformado — de uma versão antiga do cliente, de um bug, de alguém a brincar —
 * não pode rebentar o loop nem entrar como `any` no estado do jogo.
 */
export function g4Parse(data: unknown): G4Msg | null {
  if (typeof data !== "object" || data === null) return null;
  const m = data as Record<string, unknown>;
  switch (m["k"]) {
    case "g4:hello":
      return { k: "g4:hello", by: String(m["by"] ?? "") };
    case "g4:sync": {
      const seats = Array.isArray(m["seats"]) ? (m["seats"] as unknown[]) : [];
      const moves = Array.isArray(m["moves"]) ? (m["moves"] as unknown[]).map(num) : [];
      return {
        k: "g4:sync",
        to: String(m["to"] ?? ""),
        seats: [id(seats[0]), id(seats[1])],
        moves,
      };
    }
    case "g4:sit": {
      const slot = num(m["slot"]);
      if (slot !== 1 && slot !== 2) return null;
      return { k: "g4:sit", by: String(m["by"] ?? ""), slot };
    }
    case "g4:stand":
      return { k: "g4:stand", by: String(m["by"] ?? "") };
    case "g4:mv":
      return { k: "g4:mv", by: String(m["by"] ?? ""), col: num(m["col"]), n: num(m["n"]) };
    case "g4:new":
      return { k: "g4:new", by: String(m["by"] ?? "") };
    default:
      return null;
  }
}

/** O pacote da minha jogada, já com o número de ordem que lhe pertence. */
export function g4MoveMsg(state: TableState, by: string, col: number): G4Msg {
  return { k: "g4:mv", by, col, n: state.moves.length };
}

/** Quem é a cor `p` nesta mesa (identidade, ou null se o lugar está vazio). */
export function g4PlayerAt(state: TableState, p: C4Player): string | null {
  return state.seats[p - 1] ?? null;
}
